if (!customElements.get('dd0-testimonials-slider')) {
  customElements.define('dd0-testimonials-slider', class extends HTMLElement {
    connectedCallback() {
      this._controller = new AbortController();
      const { signal } = this._controller;
      this._track = this.querySelector('.dd0-tslider__track');
      this._slides = Array.from(this.querySelectorAll('.dd0-tslider__slide'));
      this._dots = Array.from(this.querySelectorAll('.dd0-tslider__dot'));
      this._arrows = Array.from(this.querySelectorAll('.dd0-tslider__arrow'));
      this._current = 0;
      this._count = this._slides.length;
      this._step = 0;
      this._timer = null;
      this._scrollRaf = null;

      if (!this._track || this._count === 0) return;

      this._dots.forEach((dot, i) => dot.addEventListener('click', () => this._goTo(i), { signal }));

      if (this._arrows.length === 2) {
        this._arrows[0].addEventListener('click', () => this._goTo(this._current - 1), { signal });
        this._arrows[1].addEventListener('click', () => this._goTo(this._current + 1), { signal });
      }

      this._track.addEventListener('scroll', this._onScroll.bind(this), { passive: true, signal });

      // Measure once, then only on resize — avoids forced reflow on every scroll tick.
      this._measure();
      if ('ResizeObserver' in window) {
        this._ro = new ResizeObserver(() => this._measure());
        this._ro.observe(this._track);
      }

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this._behavior = reduceMotion ? 'auto' : 'smooth';

      if (this.dataset.autoplay === 'true' && !reduceMotion && this._count > 1) {
        const speed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
        this._startAutoplay(speed);
        this.addEventListener('pointerenter', () => this._stopAutoplay(), { signal });
        this.addEventListener('pointerleave', () => this._startAutoplay(speed), { signal });
        this.addEventListener('focusin', () => this._stopAutoplay(), { signal });
        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) this._startAutoplay(speed);
        }, { signal });
      }
    }

    disconnectedCallback() {
      this._controller?.abort();
      this._stopAutoplay();
      if (this._ro) this._ro.disconnect();
      if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf);
      this._scrollRaf = null;
    }

    _measure() {
      const slide = this._slides[0];
      if (!slide) return;
      const gap = parseFloat(getComputedStyle(this._track).columnGap) || 0;
      this._step = slide.offsetWidth + gap;
      const maxScroll = Math.max(0, this._track.scrollWidth - this._track.clientWidth);
      this._maxIndex = this._step ? Math.round(maxScroll / this._step) : 0;
      this._current = this._step ? Math.min(this._maxIndex, Math.max(0, Math.round(this._track.scrollLeft / this._step))) : 0;
      this._updateDots(this._current);
    }

    _goTo(index) {
      if (this._count === 0) return;
      this._current = Math.min(this._maxIndex, Math.max(0, index));
      this._track.scrollTo({ left: this._step * this._current, behavior: this._behavior || 'smooth' });
      this._updateDots(this._current);
    }

    _onScroll() {
      if (this._scrollRaf) return;
      this._scrollRaf = requestAnimationFrame(() => {
        this._scrollRaf = null;
        if (!this._step) return;
        const index = Math.min(this._maxIndex, Math.max(0, Math.round(this._track.scrollLeft / this._step)));
        if (index !== this._current) {
          this._current = index;
          this._updateDots(index);
        }
      });
    }

    _updateDots(index) {
      if (this._arrows.length === 2) {
        this._arrows[0].disabled = index === 0;
        this._arrows[1].disabled = index >= this._maxIndex;
      }
      this._dots.forEach((dot, i) => {
        const active = i === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }

    _startAutoplay(speed) {
      if (!this.isConnected || this.matches(':hover') || this.contains(document.activeElement)) return;
      this._stopAutoplay();
      this._timer = setInterval(() => {
        if (!document.hidden) this._goTo(this._current >= this._maxIndex ? 0 : this._current + 1);
      }, speed);
    }

    _stopAutoplay() {
      if (this._timer) {
        clearInterval(this._timer);
        this._timer = null;
      }
    }
  });
}
